import { useState, useEffect, useRef } from 'react';
import { Modal, Stack, Select, Text, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { Procedure } from '@medplum/fhirtypes';
import { isEqual } from 'lodash';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { getServiceName, setEndDate, setStatus } from '@/utils/procedure.utils';
import { STOP_REASONS } from '@consts/medication.consts';

export interface StopTherapeuticServiceModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (stoppedService: Procedure) => void;
  /** Function to stop service on server. If provided, modal handles async save. */
  onStopToServer?: (
    procedure: Procedure,
    endDate: Date,
    reason?: string
  ) => Promise<Procedure>;
  procedure?: Procedure;
}

export const StopTherapeuticServiceModal = ({
  opened,
  onClose,
  onConfirm,
  onStopToServer,
  procedure,
}: StopTherapeuticServiceModalProps) => {
  const { t } = useTranslation(['patient-pages']);

  const [endDate, setEndDateState] = useState<Date | null>(new Date());
  const [reasonType, setReasonType] = useState<string | null>(null);
  const [customReason, setCustomReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Refs for FormActionButtons pattern
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<{
    endDate: Date | null;
    reasonType: string | null;
    customReason: string;
  } | null>(null);
  const [baselineSet, setBaselineSet] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      setEndDateState(new Date());
      setReasonType(null);
      setCustomReason('');
      setBaselineSet(true); // Flag to capture baseline after state is set
    } else {
      setBaselineSet(false);
    }
  }, [opened]);

  // Capture baseline after form state is initialized
  useEffect(() => {
    if (opened && baselineSet) {
      baselineRef.current = { endDate: endDate, reasonType, customReason };
      setBaselineSet(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate, reasonType, customReason]);

  // Change detection for FormActionButtons
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }
    try {
      return !isEqual(
        { endDate, reasonType, customReason },
        baselineRef.current
      );
    } catch {
      return true;
    }
  };

  // Discard handler for FormActionButtons
  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setEndDateState(baselineRef.current.endDate);
      setReasonType(baselineRef.current.reasonType);
      setCustomReason(baselineRef.current.customReason);
    }
  };

  const handleConfirm = async () => {
    if (!endDate || !procedure) {
      return;
    }

    let reason: string | undefined;
    if (reasonType === 'other') {
      reason = customReason || undefined;
    } else if (reasonType) {
      reason = t(`medications.stopReasons.${reasonType}`, {
        defaultValue: reasonType,
      });
    }

    // If onStopToServer is provided, save to server immediately
    if (onStopToServer) {
      setSaving(true);
      try {
        const stoppedProc = await onStopToServer(procedure, endDate, reason);
        onConfirm(stoppedProc);
        onClose();
      } catch (error) {
        notifications.show({
          color: 'red',
          title: t('therapeuticServices.stopModal.saveErrorTitle', {
            defaultValue: 'Failed to stop service',
          }),
          message: String(error),
        });
      } finally {
        setSaving(false);
      }
    } else {
      // Fallback to local-only confirm - build stopped procedure
      let stoppedProc = setStatus(procedure, 'stopped');
      stoppedProc = setEndDate(stoppedProc, endDate);
      if (reason) {
        stoppedProc = {
          ...stoppedProc,
          outcome: { text: reason },
        };
      }
      onConfirm(stoppedProc);
      onClose();
    }
  };

  const serviceName = procedure
    ? getServiceName(procedure) ||
      t('therapeuticServices.unknownService', {
        defaultValue: 'Unknown service',
      })
    : t('therapeuticServices.unknownService', {
        defaultValue: 'Unknown service',
      });

  const isValid = endDate !== null;

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={t('therapeuticServices.stopModal.title', {
        defaultValue: 'Stop Therapeutic Service',
      })}
      size="md"
    >
      <Stack gap="md">
        <Text size="sm">
          {t('therapeuticServices.stopModal.description', {
            service: serviceName,
            defaultValue: 'Stop {{service}}?',
          })}
        </Text>

        {/* End Date */}
        <DateInputWrapper
          name="stopDate"
          label={t('therapeuticServices.stopModal.endDateLabel', {
            defaultValue: 'End Date',
          })}
          value={endDate}
          onChange={setEndDateState}
          required
        />

        {/* Reason Selection */}
        <Select
          label={t('therapeuticServices.stopModal.reasonLabel', {
            defaultValue: 'Reason (optional)',
          })}
          placeholder={t('therapeuticServices.stopModal.reasonPlaceholder', {
            defaultValue: 'Select a reason',
          })}
          data={STOP_REASONS.map((reason) => ({
            value: reason,
            label: t(`medications.stopReasons.${reason}`, {
              defaultValue: reason,
            }),
          }))}
          value={reasonType}
          onChange={setReasonType}
          clearable
        />

        {/* Custom reason input when "other" is selected */}
        {reasonType === 'other' && (
          <TextInput
            label={t('therapeuticServices.stopModal.customReasonLabel', {
              defaultValue: 'Custom Reason',
            })}
            placeholder={t(
              'therapeuticServices.stopModal.customReasonPlaceholder',
              {
                defaultValue: 'Enter reason...',
              }
            )}
            value={customReason}
            onChange={(e) => setCustomReason(e.currentTarget.value)}
          />
        )}

        {/* Actions */}
        <FormActionButtons
          ref={formActionsRef}
          onSave={handleConfirm}
          onDiscard={handleDiscardChanges}
          onClose={onClose}
          hasUnsavedChanges={hasUnsavedChanges()}
          isLoading={saving}
          saveLabel={t('therapeuticServices.stopModal.confirmButton', {
            defaultValue: 'Stop Service',
          })}
          saveButtonProps={{ color: 'red', disabled: !isValid }}
        />
      </Stack>
    </Modal>
  );
};
